import { CSSProperties } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/**
 * Shimmering placeholders for the tenant template, shaped like the content
 * they stand in for so nothing shifts when data arrives. They only paint after
 * ~160ms (see `.tp-skeleton`), so quick loads never flash grey bars.
 */

export function Skel({ className, style }: { className?: string; style?: CSSProperties }) {
  return <Skeleton className={cn("tp-skel", className)} style={style} aria-hidden />;
}

/** Text lines sized in the surrounding font (wrap in .tp-display / .tp-lead …). */
export function SkelLines({ lines = 3, widths = ["100%", "94%", "62%"], className }: { lines?: number; widths?: string[]; className?: string }) {
  return (
    <span className={cn("block", className)} aria-hidden>
      {Array.from({ length: lines }).map((_, index) => (
        <Skel key={index} className="tp-skel-line" style={{ width: widths[index % widths.length] }} />
      ))}
    </span>
  );
}

/** Screen-reader status for a loading region. */
export function LoadingLabel({ children = "Loading" }: { children?: string }) {
  return (
    <span className="sr-only" role="status">
      {children}
    </span>
  );
}

/** Card with an image + title/price lines — homepage and Services page cards. */
export function MediaCardSkeleton({ aspect = "aspect-[4/3]", lines = 2 }: { aspect?: string; lines?: number }) {
  return (
    <div className="tp-card flex flex-col p-2.5" aria-hidden>
      <Skel className={cn("tp-media--inner rounded-[calc(var(--tp-radius-card)-0.4rem)]", aspect)} />
      <div className="flex flex-1 items-end justify-between gap-4 px-3 pb-3 pt-5">
        <div className="min-w-0 flex-1 space-y-2.5">
          <Skel className="h-5 w-3/5 tp-skel-round" />
          {lines > 1 && <Skel className="h-3.5 w-2/5 tp-skel-round" />}
          {lines > 2 && <Skel className="h-3.5 w-4/5 tp-skel-round" />}
        </div>
        <Skel className="h-10 w-10 shrink-0 tp-skel-round" />
      </div>
    </div>
  );
}

/** Quote card used by the reviews section. */
export function ReviewCardSkeleton() {
  return (
    <div className="tp-card flex flex-col p-7 md:p-8" aria-hidden>
      <Skel className="h-7 w-7 rounded-md" />
      <div className="mt-5 flex-1 space-y-3">
        <Skel className="h-4 w-full tp-skel-round" />
        <Skel className="h-4 w-[94%] tp-skel-round" />
        <Skel className="h-4 w-[88%] tp-skel-round" />
        <Skel className="h-4 w-1/2 tp-skel-round" />
      </div>
      <div className="mt-7 flex items-center gap-3 border-t tp-hairline pt-5">
        <Skel className="h-11 w-11 shrink-0 tp-skel-round" />
        <div className="flex-1 space-y-2">
          <Skel className="h-3.5 w-24 tp-skel-round" />
          <Skel className="h-3 w-16 tp-skel-round" />
        </div>
      </div>
    </div>
  );
}
